import {
  ReactElement,
  useState,
  useEffect,
  ChangeEvent,
  SyntheticEvent
} from 'react'
import { useHistory } from 'react-router-dom'
import { InputGroup, FormControl, Modal } from 'react-bootstrap'
import {
  RegisterWrapper,
  Container,
  FormContainer,
  Title,
  ResendOTPLink,
  VerifyOTPButton,
  BackToLoginLink,
  ChevronLeftIcon
} from './subcomponents'
import strings from '../../locale/en'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../redux/store'
import {
  saveRegistration,
  updateOtp,
  updateHasError,
  updateOtpModel,
  getAllPrivateCourses
} from '../../redux/register/action'
import { Small } from '../../typography'
import validateRegistrationForm from './helper'
import ROUTES from '../../const/routes'
import { TostMessage, Button } from '../../components'
import triggerOtpVerification, { validateOTP, sendOtp } from './otpVerification'
import { RegisterField } from './typings'
import { hasFormError } from '../../helpers/formValidation'

const Register = (): ReactElement => {
  const {
    showOtpModel,
    userDetails: { phone: phoneNumber, type = 'student' },
    hasError = false
  } = useSelector((state: RootState) => state.register, shallowEqual)

  const dispatch = useDispatch()
  const history = useHistory()
  const [otp, setOtp] = useState('')

  const initialValues = {
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  }

  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState({} as Record<string, string>)

  const {
    register: { title, submit, registerFailed, enterOtp, backToLogin },
    modal: {
      otp: { title: otpTitle, helpText, resendAgain },
      verify,
      cancel
    }
  } = strings

  const handleBlur = (field: RegisterField): void => {
    setErrors(
      validateRegistrationForm({
        values,
        errors,
        field
      })
    )
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()
    const validationError = validateRegistrationForm({
      values,
      errors
    })

    if (hasFormError(validationError)) {
      setErrors(validationError)
    } else {
      dispatch(saveRegistration({ ...values, type }))
    }
  }

  const validateOtpHandler = () => {
    validateOTP(
      otp,
      () => {
        dispatch(updateOtp('verified'))
        dispatch(getAllPrivateCourses())

        history.push(`/package/${type}`)
      },
      () => {
        dispatch(updateOtp('unverified'))
      }
    )
  }

  useEffect(() => {
    if (showOtpModel) {
      triggerOtpVerification(phoneNumber)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtpModel])

  return (
    <RegisterWrapper>
      <Container className="pt-3">
        <TostMessage
          show={hasError}
          onCloseHandler={() => {
            dispatch(updateHasError(false))
            history.push(ROUTES.LOGIN)
          }}
          message={registerFailed}
        ></TostMessage>
        <Title>{title}</Title>
        <FormContainer noValidate onSubmit={handleSubmit}>
          <div id="captcha"></div>
          <InputGroup className="mb-3">
            <FormControl
              type="text"
              name="name"
              value={values.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, name: e.target.value })
              }
              onBlur={() => handleBlur('name')}
              isValid={!errors.name}
              isInvalid={!!errors.name}
              placeholder="Name"
            />
            <FormControl.Feedback type="invalid">
              {errors.name}
            </FormControl.Feedback>
          </InputGroup>
          <InputGroup className="mb-3">
            <FormControl
              type="text"
              name="phone"
              value={values.phone}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, phone: e.target.value })
              }
              onBlur={() => handleBlur('phone')}
              placeholder="Phone Number"
              isValid={!errors.phone}
              isInvalid={!!errors.phone}
            />
            <FormControl.Feedback type="invalid">
              {errors.phone}
            </FormControl.Feedback>
          </InputGroup>
          <InputGroup className="mb-3">
            <FormControl
              type="email"
              name="email"
              value={values.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, email: e.target.value })
              }
              onBlur={() => handleBlur('email')}
              placeholder="Email"
              isValid={!errors.email}
              isInvalid={!!errors.email}
            />
            <FormControl.Feedback type="invalid">
              {errors.email}
            </FormControl.Feedback>
          </InputGroup>
          <InputGroup className="mb-3">
            <FormControl
              type="password"
              name="password"
              value={values.password}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, password: e.target.value })
              }
              onBlur={() => handleBlur('password')}
              placeholder="Password"
              isValid={!errors.password}
              isInvalid={!!errors.password}
            />
            <FormControl.Feedback type="invalid">
              {errors.password}
            </FormControl.Feedback>
          </InputGroup>
          <InputGroup className="mb-3">
            <FormControl
              type="password"
              name="confirmPassword"
              value={values.confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setValues({ ...values, confirmPassword: e.target.value })
              }
              onBlur={() => handleBlur('confirmPassword')}
              placeholder="Confirm Password"
              isValid={!errors.confirmPassword}
              isInvalid={!!errors.confirmPassword}
            />
            <FormControl.Feedback type="invalid">
              {errors.confirmPassword}
            </FormControl.Feedback>
          </InputGroup>
          <Button
            disabled={hasFormError(errors)}
            className="mb-3 mt-3"
            variant="primary"
            type="submit"
          >
            {submit}
          </Button>
          <BackToLoginLink to={ROUTES.LOGIN}>
            <ChevronLeftIcon icon={['fas', 'angle-double-left']} size="1x" />
            {backToLogin}
          </BackToLoginLink>
        </FormContainer>
      </Container>
      {showOtpModel && (
        <Modal show={showOtpModel}>
          <Modal.Header>
            <Modal.Title>{otpTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <InputGroup className="mb-3">
              <FormControl
                type="text"
                name="otp"
                value={otp}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setOtp(event?.target?.value)
                }
                placeholder={enterOtp}
              />
              <FormControl.Feedback type="invalid">{}</FormControl.Feedback>
            </InputGroup>
            <Small>
              {helpText}
              <ResendOTPLink onClick={() => sendOtp(phoneNumber)}>
                {resendAgain}
              </ResendOTPLink>
            </Small>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => dispatch(updateOtpModel(false))}
            >
              {cancel}
            </Button>
            <VerifyOTPButton onClick={validateOtpHandler} variant="primary">
              {verify}
            </VerifyOTPButton>
          </Modal.Footer>
        </Modal>
      )}
    </RegisterWrapper>
  )
}

export default Register
